import React, { useContext, useState } from 'react';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import {
  formatAmountForDisplay,
  formatAgreementTypeForDisplay,
  sumOf,
  formatTwoDecimalAmountNoRounding,
  addCommas,
  handleInputFormat
} from '../../flows/shared/utils';
import { removeCommasAndParseFloat, randomKey } from '../../core/util';
import styles from './style.css';

const TrustAgreementAllocationsScreen = () => {
  const { appState, next, back, flowProps } = useContext(FlowContext);
  const {
    store: { chosenInvestments, existingAgreements }
  } = appState;
  // set selected existing agreement for reference to static agreement amounts
  const selectedExistingAgreement =
    existingAgreements[chosenInvestments[0].registrationGroupKey];
  const [investments, updateInvestments] = useState(
    chosenInvestments.map(i => ({ ...i, percent: 100, randomKey: randomKey() }))
  );
  const { title = 'Edit your selection' } = flowProps;

  // get existing account type from chosen existing agreement investments
  const accountType = formatAgreementTypeForDisplay(
    investments[0].agreementType
  );
  // amount variable to track static total of agreements available assets
  const totalExistingAccountBalance = sumOf(
    selectedExistingAgreement.map(fund => fund.balanceAsOf)
  );
  // amount variable to track dynamic total of assets user wants to invest
  const [updatedTotalAmount, updateAmount] = useState(
    sumOf(chosenInvestments.map(fund => fund.balanceAsOf))
  );

  const [, setAmountValue] = useState('');

  // onchange for changing percentage
  // updates percentage and dollar amount based on new percent of available assets
  // format for percentage is an integer from 0 to 100, no decimals
  const percentChanged = (fund, index, event) => {
    let inputVal =
      parseFloat(event?.target?.value.replace(/[^\d].*/g, '')) || 0;
    if (inputVal > 100) inputVal = 100;
    const percentField = document.getElementById(`${fund.agreementID}-percent`);
    percentField.value = inputVal;
    const newAmountByPercentage = parseFloat(
      fund.balanceAsOf * (inputVal / 100)
    );
    const newAmountNotRounded = formatTwoDecimalAmountNoRounding(
      newAmountByPercentage
    );
    const dollarField = document.getElementById(`${fund.agreementID}-dollars`);
    dollarField.value = addCommas(newAmountNotRounded);
    const updatedFund = {
      ...investments[index],
      percent: inputVal,
      balanceAsOf: newAmountNotRounded
    };
    investments[index] = updatedFund;
    updateInvestments(investments);
    updateAmount(sumOf(investments.map(i => i.balanceAsOf)));
  };

  // onchange for changing dollar amount
  // updates dollar amount and percentage based on new amount
  // format is for amount to be 2 decimals, without rounding
  const dollarChanged = (fund, index, event) => {
    const inputVal = removeCommasAndParseFloat(event?.target?.value) || 0;
    let newAmountNotRounded = formatTwoDecimalAmountNoRounding(inputVal);
    const dollarField = document.getElementById(`${fund.agreementID}-dollars`);
    if (Number(inputVal) > fund.balanceAsOf) {
      newAmountNotRounded = String(fund.balanceAsOf);
      dollarField.value = addCommas(newAmountNotRounded);
    }
    if (event?.target?.value === '') dollarField.value = '0';
    dollarField.value = addCommas(newAmountNotRounded);
    const percentByNewAmount =
      parseInt((newAmountNotRounded / fund.balanceAsOf) * 1000, 10) / 10;
    const percentField = document.getElementById(`${fund.agreementID}-percent`);
    percentField.value = percentByNewAmount;
    const updatedFund = {
      ...investments[index],
      percent: percentByNewAmount,
      balanceAsOf: newAmountNotRounded
    };
    investments[index] = updatedFund;
    updateInvestments(investments);
    updateAmount(sumOf(investments.map(i => i.balanceAsOf)));
  };

  return (
    <div className="container">
      <Heading text={title} />
      <SubHeading text="Use percentages or dollar amounts to indicate how much to draw from each fund." />
      <div className="accounts-container">
        <div className="account-funds-table">
          <table>
            <tbody>
              <tr key="table-column-headers">
                <th>{accountType}</th>
                <th>Est. available assets</th>
                <th>Percent</th>
                <th>Est. assets to invest</th>
              </tr>
              {/*
              /* chosenInvestments to be referenced for dynamic data/input fields, i.e. percent and assets to invest
              /* existing agreements to be referenced for static data, i.e. agreement's available assets
              */}
              {selectedExistingAgreement.map((agreement, index) => (
                <tr
                  key={`${agreement.agreementID}-${chosenInvestments[index].randomKey}`}>
                  <td>
                    {agreement.fundName} ({agreement.ticker})
                  </td>
                  <td>{formatAmountForDisplay(agreement.balanceAsOf)}</td>
                  <td className="input-cell percent-input">
                    <InputField
                      key={`${chosenInvestments[index].randomKey}-percent`}
                      id={`${chosenInvestments[index].agreementID}-percent`}
                      data-name={`${chosenInvestments[index].agreementID}-percent`}
                      name="fund-allo-percent"
                      title=""
                      defaultValue={
                        chosenInvestments[index].percent ||
                        parseInt(
                          (chosenInvestments[index].balanceAsOf /
                            agreement.balanceAsOf) *
                            1000,
                          10
                        ) / 10
                      }
                      required
                      onChange={e => percentChanged(agreement, index, e)}
                    />
                  </td>
                  <td className="input-cell dollar-input">
                    <InputField
                      key={`${chosenInvestments[index].randomKey}-dollars`}
                      id={`${chosenInvestments[index].agreementID}-dollars`}
                      data-name={`${chosenInvestments[index].agreementID}-dollars`}
                      name="fund-allo-dollars"
                      title=""
                      defaultValue={addCommas(
                        String(chosenInvestments[index].balanceAsOf)
                      )}
                      required
                      onChange={e => {
                        dollarChanged(agreement, index, e);
                        handleInputFormat(
                          e,
                          'DECIMAL_CURRENCY',
                          setAmountValue
                        );
                      }}
                    />
                  </td>
                </tr>
              ))}
              <tr key="all-amount-totals" className="totals-row">
                <td>Total assets:</td>
                <td>{formatAmountForDisplay(totalExistingAccountBalance)}</td>
                <td>Total to invest:</td>
                <td>{formatAmountForDisplay(updatedTotalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="button-container">
        <MFBeaconButton
          variant="primary"
          size="normal"
          onClick={next(investments)}
          id="trustAgreementAllocationsContinue"
          label="Continue"
        />
        <MFBeaconButton
          variant="secondary"
          size="normal"
          onClick={back()}
          className="beacon-button-margin-override"
          id="trustAgreementAllocationsBack"
          label="Back"
        />
        <PhoneNumber screen="InvestmentScreen" />
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

export default TrustAgreementAllocationsScreen;
